import { Mutation, MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'

import { captureApiError } from '../utils/captureApiError'
import { classifyApiError, isSilentApiError } from '../utils/classifyApiError'
import { MutationMetaWithSilentKindsInterface } from './RestQueryClientProvider.types'

/**
 * Centralized error handler for all React Query queries and mutations.
 *
 * Decision table:
 *  abort        → drop silently (no Sentry, no notification)
 *  unauthorized → drop silently (redirect handled by AuthProvider/RestClient callback)
 *  rate_limit   → drop silently
 *  network      → Sentry
 *  forbidden    → Sentry with context tag
 *  client       → Sentry with context tag (unless mutation.meta.silentKinds includes 'client')
 *  server       → Sentry with context tag
 *  parse        → Sentry with context tag (contract/integration bug)
 *  unknown      → Sentry with context tag
 */
const handleQueryError = (error: unknown): void => {
  const { kind, status } = classifyApiError(error)

  if (isSilentApiError(kind)) {
    return
  }

  captureApiError(error, { kind, status })
}

const handleMutationError = (
  error: unknown,
  _variables: unknown,
  _context: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>,
): void => {
  const { kind, status } = classifyApiError(error)

  if (isSilentApiError(kind)) {
    return
  }

  const meta = mutation.options.meta as MutationMetaWithSilentKindsInterface | undefined

  if (meta?.silentKinds?.includes(kind)) {
    return
  }

  captureApiError(error, { kind, status })
}

export const createQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: false,
      },
    },
    mutationCache: new MutationCache({ onError: handleMutationError }),
    queryCache: new QueryCache({ onError: handleQueryError }),
  })

export const queryClient = createQueryClient()
